import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, Loader2, Stethoscope, AlertCircle, Building2, ShieldCheck, CalendarClock, BarChart3 } from 'lucide-react';
import { getSubdomain, capitalizeFirstLetter } from '../../utils/utils';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../utils/toast';

interface LoginProps { }

const Login: React.FC<LoginProps> = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const { login, message } = useAuth();

  useEffect(() => {
    console.log('=== LOGIN COMPONENT MOUNTED ===');
    console.log('Current URL:', window.location.href);
    console.log('Current authToken:', localStorage.getItem('authToken'));

    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');

    console.log('Email param:', emailParam);

    // Set email if provided
    if (emailParam) {
      console.log('Setting email from param:', emailParam);
      setEmail(decodeURIComponent(emailParam));
    }

    setSubdomain(getSubdomain);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    console.log('=== LOGIN FORM SUBMITTED ===');
    console.log('Email:', email);
    console.log('Subdomain:', subdomain);

    try {
      const success = await login(email, password, subdomain);
      if (!success) {
        toast.error(message || "Login failed. Please check your credentials.");
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  console.log('=== LOGIN COMPONENT RENDERING ===');
  console.log('Current state - email:', email, 'subdomain:', subdomain);

  const features = [
    { icon: CalendarClock, text: 'Plan and track field visits in real time' },
    { icon: Building2, text: 'Centralized doctor & firm database' },
    { icon: BarChart3, text: 'Actionable reports across your territory' },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <style>{`
        @keyframes loginCardIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .login-card-in { animation: loginCardIn 0.35s ease-out; }
      `}</style>

      {/* Branding panel */}
      <div className="relative flex-col justify-between hidden w-1/2 p-12 overflow-hidden text-white lg:flex bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600">
        <div className="absolute rounded-full -top-24 -left-24 w-72 h-72 bg-white/10 blur-3xl" />
        <div className="absolute rounded-full -bottom-32 -right-16 w-96 h-96 bg-teal-400/20 blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="flex items-center justify-center w-11 h-11 shadow-lg bg-white/15 backdrop-blur rounded-xl ring-1 ring-white/20">
            <Stethoscope className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-lg font-bold leading-tight">Apex Med</p>
            <p className="text-xs text-blue-100">Admin Portal</p>
          </div>
        </div>

        <div className="relative max-w-md">
          <h2 className="mb-4 text-3xl font-bold leading-tight">
            Run your field force with clarity and confidence.
          </h2>
          <p className="mb-8 text-blue-100">
            One workspace for visit planning, doctor engagement, and performance reporting — built for pharma teams.
          </p>
          <ul className="space-y-4">
            {features.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/15">
                  <Icon className="w-4 h-4" />
                </span>
                <span className="text-sm text-blue-50">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-blue-100">© {new Date().getFullYear()} Apex Med. All rights reserved.</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center flex-1 p-4 sm:p-8">
        <div className="w-full max-w-sm login-card-in">
          {/* Compact brand header for mobile */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="flex items-center justify-center w-11 h-11 bg-blue-600 rounded-xl">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-bold leading-tight text-gray-900">Apex Med</p>
              <p className="text-xs text-blue-600">Admin Portal</p>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="mb-1.5 text-2xl font-bold text-gray-900">Welcome back</h1>
            <p className="text-sm text-gray-500">Sign in to manage your workspace</p>
            {subdomain && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 mt-3 text-xs font-medium text-blue-700 rounded-full bg-blue-50 ring-1 ring-inset ring-blue-100">
                <Building2 className="w-3.5 h-3.5" />
                {capitalizeFirstLetter(subdomain)}
              </span>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2.5 p-3.5 mb-6 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block mb-1.5 text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3.5 top-1/2" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full py-2.5 pl-10 pr-3.5 text-sm text-gray-900 transition-all bg-white border border-gray-300 rounded-lg outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block mb-1.5 text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3.5 top-1/2" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full py-2.5 pl-10 pr-10 text-sm text-gray-900 transition-all bg-white border border-gray-300 rounded-lg outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute text-gray-400 -translate-y-1/2 right-3.5 top-1/2 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center justify-center w-full gap-2 px-4 py-2.5 text-sm font-semibold text-white transition-all bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <p className="flex items-center justify-center gap-1.5 mt-6 text-xs text-gray-400">
            <ShieldCheck className="w-3.5 h-3.5" />
            Your connection to this workspace is secure
          </p>

          <div className="mt-6 text-center">
            <a href="https://front.aplexiapharma.com/" className="text-sm font-medium text-gray-500 transition-colors hover:text-blue-600">
              ← Back to home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
